import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { SkeletonItem } from '@databases/skeleton';
import React, { forwardRef, Ref } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import EntryCardContainer, {
  entryCardShadowProps,
} from '../entry-card/EntryCardContainer';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    display: flex;
    flex-direction: column;
  }
`;

const StyledEntryCardContainer = styled(EntryCardContainer)`
  flex-grow: 1;
  width: 100%;
  padding: 0;
  min-height: 32px;
`;

const StyledButton = styled(Button)`
  ${entryCardShadowProps}

  && {
    height: auto;
    align-items: center;

    > * {
      flex-grow: 0;
    }
  }
`;

interface AddEntryCardProps {
  onAdd: () => void;
}

const AddEntryCard = forwardRef<HTMLElement, AddEntryCardProps>(function AddEntryCard(
  { onAdd },
  ref
) {
  const { showPanel } = usePanel();

  const { formatMessage } = useIntl();

  const handleClick = useFlaggingCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!isModifierKeyPressed(e)) {
        onAdd();
        e.preventDefault();
        showPanel('CreateEntryPanel');
      }
    },
    [onAdd, showPanel]
  );

  return (
    <StyledSkeletonItem isButton>
      <StyledEntryCardContainer ref={ref as Ref<HTMLDivElement>}>
        <StyledButton
          onClick={handleClick}
          iconBefore={<AddIcon label="" />}
          appearance="subtle"
        >
          {formatMessage(messages['entry-card.add-entry-text'])}
        </StyledButton>
      </StyledEntryCardContainer>
    </StyledSkeletonItem>
  );
});

export default AddEntryCard;
